// Module-init drift validator runs against NODE_CATEGORIES so any future
// category added without a corresponding tab mapping fails the dev build
// instead of silently mis-bucketing nodes. Same pattern as the picker /
// validator drift guards in soarNodes.js + v3Validate.js.
import { NODE_CATEGORIES as _SCHEMA_NODE_CATEGORIES } from '../../data/soarNodes';

/**
 * TaskPicker.search — pure scoring + index logic.
 *
 * Spec: agentlets-production/CANVAS_PICKER_INLINE_DESIGN.md §2.2.
 *
 * The picker pre-builds an in-memory index once on mount and re-uses it for
 * every keystroke. Scoring is deterministic, dependency-free, and fast on
 * realistic catalogues (~300 entries: 60 node types + 200 connector skills +
 * 30 agents + recipes). No fuzzy-search library needed at this scale.
 *
 * Index entry shape:
 *   {
 *     kind: 'type' | 'connector' | 'connector-skill' | 'agent' | 'recipe',
 *     id: string,           // unique key for keyboard nav / React lists
 *     label: string,        // human-readable display name
 *     description: string,  // sub-line; may be empty
 *     tags: string[],       // optional discovery tags
 *     // kind-specific fields:
 *     type?: string,                // node type id for kind 'type'
 *     connectorId?: string,         // installed connector UUID
 *     connectorSlug?: string,       // marketplace id (e.g. "slack")
 *     skillId?: string,             // for kind 'connector-skill'
 *     agentId?: string,             // for kind 'agent'
 *     recipeId?: string,            // for kind 'recipe'
 *     tab: string,                  // which tab this entry belongs to
 *   }
 *
 * The scoring function returns 0 (no match) or a positive number. Higher = better.
 * Caller filters out 0 and sorts descending.
 */

/**
 * Lowercase + trim, returning '' for non-strings.
 */
function norm(s) {
  if (typeof s !== 'string') return '';
  return s.trim().toLowerCase();
}

/**
 * Pure scoring function. Heuristics modeled on Linear / Notion's command
 * palette: prefix matches dominate, substrings count, tag matches help, and
 * description hits are tiebreakers.
 *
 * Scoring table (additive — multiple categories can apply):
 *   100  exact match on label
 *    80  word-prefix match on label   (e.g. "ht" → "HTTP request")
 *    50  substring match on label
 *    25  substring match on tags / type id / connector slug
 *    15  substring match on description
 *
 * The total is capped at 200 so a result with several mid-strength matches
 * never beats an exact-match-on-label.
 *
 * @param {string} query   — user-entered string (caller normalises whitespace)
 * @param {object} entry   — one record from the index
 * @returns {number} 0 if no match, otherwise a positive score.
 */
export function scoreEntry(query, entry) {
  const q = norm(query);
  if (!q) return 0;
  const label = norm(entry.label);
  const desc  = norm(entry.description);
  const tags  = (entry.tags || []).map(norm);
  const typeId = norm(entry.type || entry.connectorSlug || entry.skillId || '');

  let score = 0;

  if (label === q) score += 100;
  if (label.startsWith(q)) score += 80;
  // Word-prefix: any word in the label starts with q.
  const words = label.split(/\s+/);
  if (words.some((w) => w.startsWith(q))) score += 30;
  // Plain substring on label.
  if (label.includes(q)) score += 50;

  // Type id, connector slug, and tags are dense; substring is enough.
  if (typeId.includes(q)) score += 25;
  if (tags.some((t) => t.includes(q))) score += 25;

  // Description matches are weakest; treat as tiebreaker.
  if (desc.includes(q)) score += 15;

  return Math.min(score, 200);
}

/**
 * Build the search index from the platform's data sources. Returns a flat
 * array of entries, ready to be `.filter`'d + `.sort`'d by query.
 *
 * Pure function — no React, no async, no localStorage. Caller is responsible
 * for memoising the result so it doesn't rebuild every keystroke.
 *
 * @param {{
 *   nodeTypes: object,
 *   nodeCategories: object[],
 *   installedConnectors?: object[],
 *   marketplaceConnectors?: object[],
 *   agents?: object[],
 *   recipes?: object[],
 * }} sources
 * @returns {object[]} flat index
 */

// Map every node category to a picker tab. The picker groups by the
// seven tabs from the design spec. Every category declared in
// NODE_CATEGORIES (soarNodes.js) MUST appear here — the IIFE at the
// bottom of this file imports NODE_CATEGORIES and throws in dev/test
// if anything's missing, so the next devloop catches drift instead of
// shipping nodes to the wrong tab.
const CATEGORY_TO_TAB = {
  triggers:    'trigger',
  tasks:       'worker',     // catch-all for "do something" primitives
  security_ops:'worker',
  logic:       'operator',
  gates:       'operator',   // human / RBAC gates are flow control
  data:        'worker',
  agents:      'agent',
  enforcement: 'operator',
  connector:   'connector',
  // Categories added during the canvas taxonomy expansion (LLM /
  // embeddings / vector primitives, delays + signal-waits, external
  // workflow orchestrator integrations). Pre-fix, these silently fell
  // through the lookup's `|| 'worker'` default, so AI / RAG primitives
  // appeared under the Worker tab.
  ai_rag:      'ai',
  wait_signal: 'operator',   // timers + signal_wait are flow-control primitives
  external:    'connector',  // external orchestrator runs are connector-class invocations
};

export function buildIndex({
  nodeTypes,
  nodeCategories,
  installedConnectors = [],
  marketplaceConnectors = [],
  agents = [],
  recipes = [],
}) {
  const out = [];

  // CATEGORY_TO_TAB lives at module scope below — the drift IIFE at
  // the bottom asserts every category in NODE_CATEGORIES is covered.
  const AI_TYPE_HINTS = new Set([
    'llm_task', 'agent_chat', 'agent_task', 'skill_eval',
    'classify', 'extract_entities', 'summarize',
    'vector_search', 'vector_upsert', 'generate_embeddings',
    'output_validator',
  ]);

  for (const def of Object.values(nodeTypes || {})) {
    if (!def || !def.type) continue;
    // Hide internal aliases / system-only types from the picker. The
    // existing `category === 'enforcement'` flow controls those — they
    // get inserted by autoInsertEnforcementNodes, not by the user.
    const isInternalEnforcement = def.category === 'enforcement' && def._systemOnly;
    if (isInternalEnforcement) continue;
    // RC-124 — honor the same paletteHidden flag that getCategoryNodes
    // and searchNodes filter on (soarNodes.js:6519/6531). Pre-RC-124
    // the Task Picker was the third surface that drifted from the
    // schema-source-of-truth (same class of bug as RC-114's lintRawFork
    // hardcoded prefix). manual_start is the canonical entry sentinel
    // auto-seeded by makeFreshSentinelPayload (CanvasPageV3:481) —
    // operators must never be able to insert a second one. End /
    // terminate sentinels are paletteHidden for the same reason where
    // they apply. Filtering here makes the picker honor the operator-
    // facing visibility contract uniformly with the left palette and
    // the change-type picker.
    if (def.paletteHidden) continue;
    // Aliases (entries with aliasOf pointing at a canonical type) are
    // also operator-noise — they exist for back-compat with saved
    // canvases, not for fresh insertion. Same reasoning as the
    // Change-Type picker's isAlias filter (NodeTypeSwapControl).
    if (def.aliasOf) continue;

    const tab = AI_TYPE_HINTS.has(def.type) ? 'ai' : (CATEGORY_TO_TAB[def.category] || 'worker');
    out.push({
      kind: 'type',
      id: `type:${def.type}`,
      label: def.label || def.type,
      description: def.description || '',
      tags: Array.isArray(def.tags) ? def.tags : [],
      type: def.type,
      tab,
      color: def.color || '#888888',
      icon: def.icon || null,
    });
  }

  // ── Recipes ─────────────────────────────────────────────────────────
  for (const recipe of recipes) {
    if (!recipe || !recipe.id) continue;
    const flow = (recipe.nodes || []).map((n) => n.label || n.type).join(' → ');
    out.push({
      kind: 'recipe',
      id: `recipe:${recipe.id}`,
      label: recipe.name,
      description: flow,
      tags: ['recipe', 'multi-node'],
      recipeId: recipe.id,
      tab: 'for-you',
      nodeCount: (recipe.nodes || []).length,
    });
  }

  // ── Installed connectors + their skills ─────────────────────────────
  // Each installed connector becomes a parent row plus one child row per
  // skill. The picker UI groups them visually but the index treats each
  // skill as an independently searchable entry.
  for (const conn of installedConnectors) {
    if (!conn || !conn.id) continue;
    // Find the marketplace definition (skills live there).
    const slug = conn.marketplaceId || conn.type || conn.connectorType;
    const mk = marketplaceConnectors.find((m) =>
      m && (m.id === slug || m.id === conn.type || norm(m.name) === norm(conn.name))
    );
    out.push({
      kind: 'connector',
      id: `connector:${conn.id}`,
      label: conn.name || mk?.name || conn.type,
      description: mk?.description || mk?.category || 'Installed connector',
      tags: ['connector', 'installed', mk?.category || ''].filter(Boolean),
      connectorId: conn.id,
      connectorSlug: mk?.id || slug || '',
      tab: 'connector',
      skills: mk?.skills || [],
    });
    for (const skill of mk?.skills || []) {
      if (!skill || !skill.id) continue;
      out.push({
        kind: 'connector-skill',
        id: `skill:${conn.id}:${skill.id}`,
        label: skill.name || skill.id,
        description: skill.method && skill.endpoint
          ? `${skill.method} ${skill.endpoint}`
          : (skill.description || ''),
        tags: ['connector', 'skill', mk?.category || ''].filter(Boolean),
        connectorId: conn.id,
        connectorSlug: mk?.id || slug || '',
        skillId: skill.id,
        tab: 'connector',
      });
    }
  }

  // ── Marketplace connectors that are NOT installed ───────────────────
  // Show with a "not installed" hint so users know to go to /connectors;
  // they can't be picked into the workflow yet but are searchable.
  const installedSlugs = new Set(
    installedConnectors
      .map((c) => norm(c?.marketplaceId || c?.type || c?.connectorType))
      .filter(Boolean)
  );
  for (const mk of marketplaceConnectors) {
    if (!mk || !mk.id) continue;
    if (installedSlugs.has(norm(mk.id))) continue;
    if (!Array.isArray(mk.skills) || mk.skills.length === 0) continue;
    out.push({
      kind: 'connector',
      id: `connector:marketplace:${mk.id}`,
      label: mk.name || mk.id,
      description: `Not installed · ${mk.description || mk.category || ''}`,
      tags: ['connector', 'marketplace', 'not-installed', mk.category || ''].filter(Boolean),
      connectorId: null,
      connectorSlug: mk.id,
      tab: 'connector',
      skills: mk.skills,
      notInstalled: true,
    });
  }

  // ── Agents ──────────────────────────────────────────────────────────
  for (const agent of agents) {
    if (!agent || !agent.id) continue;
    out.push({
      kind: 'agent',
      id: `agent:${agent.id}`,
      label: agent.name || agent.id,
      description: [agent.role, agent.status].filter(Boolean).join(' · '),
      tags: ['agent', agent.type || '', agent.role || ''].filter(Boolean),
      agentId: agent.id,
      tab: 'agent',
      isExternal: agent.type === 'external' || agent.archetype === 'external',
      isConversational: !!agent.conversational,
    });
  }

  return out;
}

/**
 * Filter + score + sort the index against a query string. Returns the top N.
 *
 * @param {object[]} index
 * @param {string} query
 * @param {number} limit
 * @returns {object[]}
 */
export function searchIndex(index, query, limit = 50) {
  const q = (query || '').trim();
  if (!q) return [];
  const scored = [];
  for (const entry of index) {
    const s = scoreEntry(q, entry);
    if (s > 0) scored.push({ entry, score: s });
  }
  scored.sort((a, b) => b.score - a.score);
  return scored.slice(0, limit).map((x) => x.entry);
}

// ============================================================================
// Module-init drift validator
// ============================================================================
//
// Walks every category declared in NODE_CATEGORIES and asserts that the
// CATEGORY_TO_TAB map covers it. Without this, adding a new node category
// to the schema would silently fall through CATEGORY_TO_TAB's `|| 'worker'`
// default and operators would see those nodes under the wrong tab. Same
// single-source-of-truth pattern as the picker validator in soarNodes.js
// and the REQUIRED_CONFIG_BY_TYPE validator in v3Validate.js.
//
// Throws in dev/test (NODE_ENV !== 'production') so devloop fails loudly
// with a precise repro. console.error in prod so production users don't
// see a blank canvas just for a config gap — they get a slightly wrong
// tab assignment, not a UI crash.

(function validateCategoryToTabAgainstSchema() {
  if (!Array.isArray(_SCHEMA_NODE_CATEGORIES)) return;
  const missing = [];
  for (const cat of _SCHEMA_NODE_CATEGORIES) {
    if (!cat || !cat.id) continue;
    if (!Object.prototype.hasOwnProperty.call(CATEGORY_TO_TAB, cat.id)) {
      missing.push(cat.id);
    }
  }
  if (missing.length === 0) return;
  const msg = `[TaskPicker.search] CATEGORY_TO_TAB drift detected — `
    + `the following NODE_CATEGORIES are not mapped to a picker tab: `
    + `${missing.join(', ')}. Add an entry to CATEGORY_TO_TAB in `
    + `TaskPicker.search.js (one of: trigger | worker | operator | ai | `
    + `connector | agent).`;
  if (typeof process !== 'undefined' && process.env && process.env.NODE_ENV !== 'production') {
    throw new Error(msg);
  } else if (typeof console !== 'undefined') {
    console.error(msg);
  }
})();
